import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ArrowLeftRight, Repeat, Landmark, Users, CalendarClock, HandCoins,
  PiggyBank, BarChart3, UsersRound, LogOut, Menu, X, Bell, BellOff, Eye,
} from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";

export default function AppLayout() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [remOn, setRemOn] = useState(user?.reminders_enabled !== false);

  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => { setRemOn(user?.reminders_enabled !== false); }, [user?.reminders_enabled]);

  const toggleRem = async () => {
    const next = !remOn;
    setRemOn(next);
    try {
      await api.post("/reminders/toggle", { enabled: next });
    } catch {
      setRemOn(!next);
    }
  };

  const items = [
    { to: "/app", icon: LayoutDashboard, key: "dashboard", end: true },
    { to: "/app/islemler", icon: ArrowLeftRight, key: "transactions" },
    { to: "/app/tekrarlayan", icon: Repeat, key: "recurring" },
    { to: "/app/bankalar", icon: Landmark, key: "banks" },
    { to: "/app/cari", icon: Users, key: "cari" },
    { to: "/app/odemeler", icon: CalendarClock, key: "payments" },
    { to: "/app/tahsilatlar", icon: HandCoins, key: "receivables" },
    { to: "/app/butceler", icon: PiggyBank, key: "budgets" },
    { to: "/app/raporlar", icon: BarChart3, key: "reports" },
    { to: "/app/ekip", icon: UsersRound, key: "team" },
  ];
  const current = items.find((i) => (i.end ? pathname === i.to : pathname.startsWith(i.to)));
  const initials = (user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const isViewer = (user?.team_role ?? "owner") === "viewer";

  const nav = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center border-b border-slate-200 px-6">
        <Logo markClass="h-7 w-7" wordClass="text-xs" />
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-5" data-testid="sidebar-nav">
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} end={i.end} data-testid={`sidebar-${i.key}-link`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                isActive ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              }`}>
            <i.icon className="h-4 w-4" />
            {t.app[i.key]}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-200 p-4">
        <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-900 font-display text-xs font-semibold text-white">
            {initials}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-slate-900" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="truncate text-xs text-slate-400">{user?.company || user?.email}</p>
          </div>
        </div>
        <button onClick={async () => { await logout(); navigate("/giris"); }} data-testid="sidebar-logout-button"
          className="mt-2 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600">
          <LogOut className="h-4 w-4" />
          {t.app.logout}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-slate-200 bg-white lg:block" data-testid="sidebar">
        {nav}
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl" data-testid="sidebar-mobile">
            {nav}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl lg:px-8">
          <button className="rounded-md p-2 text-slate-600 hover:bg-slate-100 lg:hidden" aria-label="Menü"
            onClick={() => setOpen(!open)} data-testid="mobile-menu-button">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <h1 className="font-display text-base font-semibold tracking-tight text-slate-900" data-testid="header-page-title">
            {current ? t.app[current.key] : "İRA Finans"}
          </h1>
          {isViewer && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-500" data-testid="viewer-mode-chip">
              <Eye className="h-3.5 w-3.5" /> {t.team.viewerNote}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={toggleRem} title={remOn ? t.rem.on : t.rem.off} aria-label={t.rem.on}
              data-testid="reminder-toggle-button"
              className={`grid h-9 w-9 place-items-center rounded-full border transition-colors ${
                remOn ? "border-brand-soft bg-brand-soft text-brand" : "border-slate-200 text-slate-400 hover:text-slate-900"
              }`}>
              {remOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            </button>
            <div className="flex items-center rounded-full border border-slate-200 p-0.5" data-testid="header-lang-toggle">
              {["tr", "en"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`lang-toggle-${l}`}
                  className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-medium uppercase transition-colors ${
                    lang === l ? "bg-slate-900 text-white" : "text-slate-400 hover:text-slate-900"
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
        </header>
        <main className="p-4 lg:p-8" data-testid="app-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
